import { useMemo } from "react";
import WellnessMetadataContext from "./contexts/WellnessMetadataContext";
import WellnessMetadataService from "services/WellnessMetadataService";
import { WellnessMetadata } from "types";
import { useTranslation } from "react-i18next";
import useQuery from "hooks/useQuery";
import WellnessMetadataStatusContext from "./contexts/WellnessMetadataStatusContext";

function useGetWellness() {
  const { data, status } = useQuery<WellnessMetadata[]>(
    ["WellnessMetadataService.getAll"],
    () => WellnessMetadataService.getAll(),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
    }
  );
  return useMemo(() => ({ data: data || [], status }), [data, status]);
}

interface WellnessMetadataProviderProps {
  children?: React.ReactNode;
}

function WellnessMetadataProvider(props: WellnessMetadataProviderProps) {
  const { data: wellness, status } = useGetWellness();
  const { t } = useTranslation();

  /**
   * @description Handle the translation of all wellness metadata names
   *
   * @important This is a special case to handle the translation, sorting and filtering of wellness metadata names
   * We should always translate as close as the render as possible
   */
  const translatedWellness = useMemo(() => {
    return wellness.map((wellness) => ({
      ...wellness,
      name: t(`wellness_name_${wellness.id}`),
    }));
  }, [wellness, t]);

  return (
    <WellnessMetadataStatusContext.Provider value={status}>
      <WellnessMetadataContext.Provider value={translatedWellness}>{props.children}</WellnessMetadataContext.Provider>
    </WellnessMetadataStatusContext.Provider>
  );
}

export default WellnessMetadataProvider;
